import { Concept } from '../../../../../models/Entities/Contracts/Concept';
import { ConceptCategory } from '../../../../../models/Entities/Contracts/ConceptCategory';
import { ConceptSponsor } from '../../../../../models/Entities/Contracts/ConceptSponsor';
import { Sponsor } from '../../../../../models/Entities/Sponsor/Sponsor';
import { AppState } from '../../../../../redux/reducers';
import * as ConceptStore from '../../../../../redux/middleware/ConceptMiddleware';
import * as SponsorConceptStore from '../../../../../redux/middleware/ConceptSponsorMiddleware';
import * as ConceptCategoryStore from '../../../../../redux/middleware/ConceptCategoryMiddleware';

export interface ConceptSponsorListOwnProps {
  sponsorId: string;
}

export interface ConceptSponsorListRow {
  rowKey: string;
  id: number;
  sponsorId: string;
  conceptId: number;
  conceptName: string;
  conceptDescription: string;
  conceptReferenceId: string;
  conceptCode: string;
  conceptType?: number;
  costCurrency: string;
  referenceCost?: number;
  sponsorWeigthedCost?: number;
  siteWeigthedCost?: number;
  countryWeigthedCost?: number;
  globalWeigthedCost?: number;
  isAssociated: boolean;
  conceptSponsorId?: number;
  conceptCategoryId?: number;
  referenceCostVariance?: number;
  sponsorWeigthedCostVariance?: number;
  siteWeigthedCostVariance?: number;
}

interface ConnectedProps {
  isLoading: boolean;
  errorMessage?: string;
  rows: ConceptSponsorListRow[];
  concepts: Concept[];
  categories: ConceptCategory[];
}

interface DispatchProps {
  GetAllConcepts: typeof ConceptStore.actionCreators.GetAllConcepts;
  GetAllConceptSponsors: typeof SponsorConceptStore.actionCreators.GetAllConceptSponsors;
  DeleteConceptSponsor: typeof SponsorConceptStore.actionCreators.DeleteConceptSponsor;
  GetAllConceptCategories: typeof ConceptCategoryStore.actionCreators.GetAllConceptCategories;
}

export type Props = ConceptSponsorListOwnProps & ConnectedProps & DispatchProps;

const normalizeSponsorId = (value?: string): string => (value || '').toLowerCase();

export const buildConceptSponsorRows = (
  sponsor: Sponsor | undefined,
  concepts: Concept[] | undefined
): ConceptSponsorListRow[] => {
  const conceptsById = new Map<number, Concept>((concepts || []).map((concept) => [concept.id, concept]));

  return (sponsor?.conceptsAssociated || [])
    .map((conceptSponsor: ConceptSponsor) => {
      const concept = conceptsById.get(conceptSponsor.conceptId);

      const sortedHistories = (concept?.conceptCostHistories || [])
        .slice()
        .sort((a, b) => new Date(b.changeDate).getTime() - new Date(a.changeDate).getTime());

      const latestReferenceHistory = sortedHistories.find(h => h.referenceCostVariancePercentage !== 0);
      const latestSponsorHistory = sortedHistories.find(h => h.sponsorWeigthedCostVariancePercentage !== 0);
      const latestSiteHistory = sortedHistories.find(h => h.siteWeigthedCostVariancePercentage !== 0);

      return {
        rowKey: `concept-sponsor-${conceptSponsor.id}`,
        id: conceptSponsor.id,
        sponsorId: conceptSponsor.sponsorId,
        conceptId: conceptSponsor.conceptId,
        conceptName: concept?.conceptName || String(conceptSponsor.conceptId),
        conceptDescription: concept?.conceptDescription || '',
        conceptReferenceId: concept?.conceptReferenceId || '',
        conceptCode: concept?.conceptCode || '',
        conceptType: concept?.conceptType,
        costCurrency: concept?.costCurrency || '',
        referenceCost: concept?.referenceCost,
        sponsorWeigthedCost: concept?.sponsorWeigthedCost,
        siteWeigthedCost: concept?.siteWeigthedCost,
        countryWeigthedCost: concept?.countryWeigthedCost,
        globalWeigthedCost: concept?.globalWeigthedCost,
        isAssociated: true,
        conceptCategoryId: concept?.conceptCategoryId,
        conceptSponsorId: conceptSponsor.id,
        referenceCostVariance: latestReferenceHistory?.referenceCostVariancePercentage,
        sponsorWeigthedCostVariance: latestSponsorHistory?.sponsorWeigthedCostVariancePercentage,
        siteWeigthedCostVariance: latestSiteHistory?.siteWeigthedCostVariancePercentage
      };
    })
    .sort((a, b) => a.conceptName.localeCompare(b.conceptName));
};

export const mapStateToProps = (state: AppState, ownProps: ConceptSponsorListOwnProps) => {
  const normalizedSponsorId = normalizeSponsorId(ownProps.sponsorId);
  const sponsor = state.Sponsor?.list.find(
    (currentSponsor) => normalizeSponsorId(currentSponsor.person?.personIdentifier) === normalizedSponsorId
  );
  const concepts = state.Concepts?.list || [];

  return {
    isLoading: Boolean(state.Sponsor?.isLoadingOne) || Boolean(state.Concepts?.isLoadingAll),
    errorMessage: state.Sponsor?.error?.ErrorMessage || state.Concepts?.error?.ErrorMessage,
    rows: buildConceptSponsorRows(sponsor, concepts),
    concepts,
    categories: state.ConceptCategories?.list || []
  };
};

export const mapDispatchToProps = {
  GetAllConcepts: ConceptStore.actionCreators.GetAllConcepts,
  GetAllConceptSponsors: SponsorConceptStore.actionCreators.GetAllConceptSponsors,
  DeleteConceptSponsor: SponsorConceptStore.actionCreators.DeleteConceptSponsor,
  GetAllConceptCategories: ConceptCategoryStore.actionCreators.GetAllConceptCategories
};
